/**
 * Zod-free task-template grammar + validation helpers.
 *
 * This is the single source of truth for the `{{merge_tag}}` pattern, the
 * field-type list, the due-offset grammar, and save-time content validation.
 * It deliberately has no zod / openapi imports so it can be runtime-exported
 * from the package root (`@quovy/types`) and consumed by client bundles —
 * the wire schemas that wrap these live in `./task-templates` (subpath
 * `@quovy/types/task-templates`, server-side consumers only).
 */

export const TASK_TEMPLATE_FIELD_TYPES = ["text", "textarea", "date", "select"] as const;

export type TaskTemplateFieldType = (typeof TASK_TEMPLATE_FIELD_TYPES)[number];

/**
 * Server-legal field-key grammar — lowercase snake_case, letter-initial. The
 * wire schema (`TaskTemplateFieldSchema.key`) enforces this same pattern (plus
 * a separate ≤64 length bound); kept here so the client-side validator can
 * surface an illegal or empty key as a located, inline error instead of
 * letting it reach the server as an unlocated 400.
 */
export const TASK_TEMPLATE_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * The one `{{merge_tag}}` grammar, shared by the save-time validator and the
 * UI's renderer/segmenter. Inner whitespace is spaces/tabs only — a "tag"
 * spanning a line break would be invisible in the per-line chip editor while
 * still real to the validator. Consume via `matchAll` only (it does not
 * mutate the shared regex's `lastIndex`; a bare `.exec()` loop would).
 */
export const MERGE_TAG_PATTERN = /\{\{[ \t]*([a-zA-Z0-9_.-]+)[ \t]*\}\}/g;

/** Distinct `{{tag}}` keys referenced in a template string. */
export function extractMergeTagKeys(template: string): string[] {
  return Array.from(new Set(Array.from(template.matchAll(MERGE_TAG_PATTERN), (m) => m[1]!)));
}

/**
 * The one due-offset grammar: "Same day", "+1 business day", "+3 days", …
 * (matched case-insensitively). Returns the parsed offset, or null when the
 * string isn't a valid offset. Both the wire schema (validation) and the UI
 * (client-side resolution to a concrete date) go through this parser so the
 * grammar can't drift between them.
 *
 * The count is capped at 4 digits (≤ 9999 days): offsets are resolved
 * client-side by an O(n) day-walk, so an unbounded count would let one
 * workspace member store a template that hangs every browser that selects it.
 */
export function parseTaskTemplateDueOffset(
  offset: string,
): { days: number; business: boolean } | null {
  const trimmed = offset.trim();
  if (/^same[ \t]+day$/i.test(trimmed)) return { days: 0, business: false };
  const match = /^\+(\d{1,4})[ \t]+(business[ \t]+)?days?$/i.exec(trimmed);
  if (!match) return null;
  return { days: Number(match[1]), business: Boolean(match[2]) };
}

/**
 * Cross-field template consistency, run at save time (create, and update
 * against the merged result) on the server and pre-save in the editor UI:
 * duplicate field keys, `{{tags}}` referencing no field ("unknown tag =
 * validation error at save time" per the design doc), `select` fields
 * without options, and `select` defaults not among the options. Returns
 * human-readable problems; empty = valid.
 */
export function validateTaskTemplateContent(input: {
  titleTemplate: string;
  descriptionTemplate: string;
  fields: readonly {
    key: string;
    type: string;
    default?: string | undefined;
    options?: readonly string[] | undefined;
  }[];
}): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const field of input.fields) {
    if (!TASK_TEMPLATE_FIELD_KEY_PATTERN.test(field.key)) {
      problems.push(
        field.key.length === 0
          ? "a field is missing a key"
          : `field key "${field.key}" must be lowercase snake_case (a-z, 0-9, _), starting with a letter`,
      );
    }
    if (seen.has(field.key)) problems.push(`duplicate field key "${field.key}"`);
    seen.add(field.key);
    if (field.type === "select") {
      if ((field.options?.length ?? 0) === 0) {
        problems.push(`select field "${field.key}" has no options`);
      } else if (field.default !== undefined && !field.options!.includes(field.default)) {
        problems.push(`select field "${field.key}" default is not one of its options`);
      }
    }
  }
  for (const [label, template] of [
    ["title_template", input.titleTemplate],
    ["description_template", input.descriptionTemplate],
  ] as const) {
    for (const key of extractMergeTagKeys(template)) {
      if (!seen.has(key)) problems.push(`${label} references unknown field "{{${key}}}"`);
    }
  }
  return problems;
}
